import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Card, Skeleton } from "@ndd/ui";
import { ExternalLink, X } from "lucide-react";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { authenticatedApi } from "@/lib/api";
import type { FormSnapshotData } from "../../../../shared/form-types";
import type { ObservationNoteData } from "../../../../shared/observation-types";
import { formatLocalDate } from "../../../../shared/local-date";
import type { MeetingNoteData } from "./meeting-note-types";
import type { ReadRowSource } from "./read-row";
import { AskAboutSourceAction } from "./ask-about-source-action";
import { FlyoverShell } from "./flyover-shell";
import { priorWeeklySnapshot } from "./form-snapshot-detail";
import { FormSnapshotFlyover } from "./form-snapshot-flyover";
import { MeetingNoteBody } from "./meeting-note-detail";
import { ObservationTagBadge } from "./observation-tag";

type RecordData = ObservationNoteData | MeetingNoteData;

function endpoint(rdId: string, source: ReadRowSource): string {
  if (source.sourceType === "observation") return `/api/rds/${rdId}/observations/${source.sourceId}`;
  return `/api/rds/${rdId}/meeting-notes/${source.sourceId}`;
}

export function RecordSourceFlyover({
  rdId,
  rdSlug,
  source,
  onClose,
}: {
  rdId: string;
  rdSlug: string;
  source: ReadRowSource | null;
  onClose: () => void;
}) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const liveForm = source !== null && source.sourceType === "form" && !source.deleted;
  // Live forms open the real FormSnapshotFlyover, which needs the full list for
  // the prior-week change line. Same query key as the forms surface pages.
  const forms = useQuery({
    queryKey: ["forms", rdSlug],
    queryFn: () => authenticatedApi<{ data: FormSnapshotData[] }>(getToken, `/api/rds/${rdSlug}/forms`),
    enabled: liveForm,
    retry: false,
  });
  const formSnapshot = liveForm
    ? forms.data?.data.find((s) => s.id === source.sourceId) ?? null
    : null;
  const formLookupFailed =
    liveForm && (forms.isError || (forms.data !== undefined && formSnapshot === null));
  const record = useQuery({
    queryKey: ["chat-source", rdId, source?.sourceType, source?.sourceId],
    queryFn: () => authenticatedApi<{ data: RecordData }>(getToken, endpoint(rdId, source!)),
    enabled: Boolean(source && !source.deleted && source.sourceType !== "session" && source.sourceType !== "form"),
    retry: false,
  });
  useEffect(() => {
    if ((!record.isError && !formLookupFailed) || !source) return;
    Sentry.captureMessage("Chat source flyover failed to load", {
      level: "error",
      tags: {
        surface: "chat_source_flyover",
        source: "client",
        source_type: source.sourceType,
        route: "rds.thread-chat",
      },
    });
  }, [record.isError, formLookupFailed, source]);

  if (liveForm && !formLookupFailed) {
    return (
      <FormSnapshotFlyover
        rdSlug={rdSlug}
        snapshot={formSnapshot}
        prior={priorWeeklySnapshot(forms.data?.data ?? [], formSnapshot)}
        onClose={onClose}
        onEdit={(formId) => {
          onClose();
          void navigate({
            to: "/rds/$rdId/forms",
            params: { rdId: rdSlug },
            search: { edit: formId },
          });
        }}
      />
    );
  }

  return (
    <FlyoverShell openKey={source ? `${source.sourceType}:${source.sourceId}` : null} onClose={onClose}>
      {source && (
        <>
          <div className="shrink-0 border-b border-border bg-card px-[22px] pb-[14px] pt-[18px]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="mb-1.5 font-sans text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-subtle">
                  Source · {source.sourceType === "meeting_note" ? "Meeting note" : source.sourceType}
                </p>
                <h3 className="font-sans text-[17px] font-bold leading-[1.3] text-foreground">
                  {source.deleted ? `Deleted ${source.sourceType === "meeting_note" ? "meeting note" : source.sourceType}` : source.title || formatLocalDate(source.date) || "Source"}
                </h3>
              </div>
              <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close source">
                <X />
              </Button>
            </div>
            {!source.deleted && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <AskAboutSourceAction rdSlug={rdSlug} source={{ type: source.sourceType, id: source.sourceId }} />
                {source.sourceType === "form" && (
                  <Button asChild variant="outline" size="xs">
                    <Link to="/rds/$rdId/forms/$formId" params={{ rdId: rdSlug, formId: source.sourceId }}>Open full form</Link>
                  </Button>
                )}
                {source.sourceType === "meeting_note" && (
                  <Button asChild variant="outline" size="xs">
                    <Link to="/rds/$rdId/meetings" params={{ rdId: rdSlug }} search={{ meetingNoteId: source.sourceId }}>Open meeting note</Link>
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto bg-background px-[22px] pb-[26px] pt-[18px]">
            {source.deleted ? (
              <Card className="p-5 text-sm text-muted-foreground">This source is no longer available. Its read receipt remains for audit history.</Card>
            ) : source.sourceType === "form" ? (
              // Only reachable when the forms-list lookup failed above.
              <Card className="p-5 text-sm text-muted-foreground">This source could not be loaded.</Card>
            ) : record.isLoading ? (
              <div className="space-y-3"><Skeleton className="h-5 w-1/2" /><Skeleton className="h-24 w-full" /></div>
            ) : record.isError || !record.data ? (
              <Card className="p-5 text-sm text-muted-foreground">This source could not be loaded.</Card>
            ) : source.sourceType === "observation" ? (
              <ObservationFlyoverBody note={record.data.data as ObservationNoteData} />
            ) : (
              <MeetingFlyoverBody note={record.data.data as MeetingNoteData} />
            )}
          </div>
        </>
      )}
    </FlyoverShell>
  );
}

function ObservationFlyoverBody({ note }: { note: ObservationNoteData }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ObservationTagBadge tag={note.tag} />
        <span className="text-xs text-subtle">{formatLocalDate(note.observationDate, "shortWithYear")} · {note.authorName || "Unknown author"}</span>
      </div>
      {note.title && <h4 className="font-sans text-lg font-semibold text-foreground">{note.title}</h4>}
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{note.body}</p>
    </div>
  );
}

function MeetingFlyoverBody({ note }: { note: MeetingNoteData }) {
  const fathomUrl = safeHttpsUrl(note.fathomUrl);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{formatLocalDate(note.meetingDate, "shortWithYear")}</Badge>
        {fathomUrl && (
          <a href={fathomUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-interactive hover:underline">
            Fathom <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <MeetingNoteBody bodyJson={note.bodyJson} />
    </div>
  );
}

function safeHttpsUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}
